import apiClient from './apiClient';

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'engagement' | 'contribution' | 'achievement' | 'special';
  criteria: {
    type: string;
    threshold?: number;
    timeframe?: string;
    additional?: any;
  };
}

export interface BadgeProgress {
  badge: BadgeDefinition;
  hasEarned: boolean;
  currentValue: number;
  maxValue: number;
  progressPercent: number;
}

export interface UserBadges {
  username: string;
  badges: BadgeDefinition[];
  count: number;
}

export interface BadgeLeaderboard {
  _id: string;
  username: string;
  badges: string[];
  badgeCount: number;
  points: number;
  reputationScore: number;
  earnedBadges: BadgeDefinition[];
}

/**
 * Get all badge definitions
 */
export const getBadgeDefinitions = async (): Promise<BadgeDefinition[]> => {
  try {
    const response = await apiClient.get('/badges/definitions');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching badge definitions:', error);
    throw error;
  }
};

/**
 * Get current user's badge progress
 */
export const getUserBadgeProgress = async (): Promise<BadgeProgress[]> => {
  try {
    const response = await apiClient.get('/badges/progress');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching user badge progress:', error);
    throw error;
  }
};

/**
 * Get badge progress for a specific user
 */
export const getUserBadgeProgressById = async (userId: string): Promise<BadgeProgress[]> => {
  try {
    const response = await apiClient.get(`/badges/progress/${userId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching user badge progress by ID:', error);
    throw error;
  }
};

/**
 * Check current user for new badges
 */
export const checkUserBadges = async (): Promise<{ newBadges: string[]; count: number }> => {
  try {
    const response = await apiClient.post('/badges/check');
    return response.data.data;
  } catch (error) {
    console.error('Error checking user badges:', error);
    throw error;
  }
};

/**
 * Get user's earned badges
 */
export const getUserBadges = async (userId: string): Promise<UserBadges> => {
  try {
    const response = await apiClient.get(`/badges/user/${userId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching user badges:', error);
    throw error;
  }
};

/**
 * Get badge leaderboard
 */
export const getBadgeLeaderboard = async (limit: number = 10): Promise<BadgeLeaderboard[]> => {
  try {
    const response = await apiClient.get(`/badges/leaderboard?limit=${limit}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching badge leaderboard:', error);
    throw error;
  }
};

/**
 * Award badge manually (Admin only)
 */
export const awardBadgeManually = async (userId: string, badgeId: string): Promise<any> => {
  try {
    const response = await apiClient.post('/badges/award', { userId, badgeId });
    return response.data.data;
  } catch (error) {
    console.error('Error awarding badge manually:', error);
    throw error;
  }
};
